import { validateLimitCost } from 'pages/Project/Common/hooks/prepareLimitsData';
import type { IUerContractOtherCostLimitSpanSelectionData } from 'pages/Project/UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { Button, IconDocFilled, IconTree, IconEdit, FieldGroup } from 'uikit';

import cn from 'classnames';
import type React from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';

import { ControlNumberField } from '../Form';
import { TooltipWrapper } from '../TooltipWrapper';

import { ContractOtherCostLimitSpanModal } from './ContractOtherCostLimitSpanModal';

import Styles from './ContractOtherCostLimitSpan.module.css';

interface IContractOtherCostLimitSpan {
    constructionTypeId: number;
    isDisabled: boolean;
    isChecked: boolean;
    defaultConstruction: boolean;
    isTooltipHidden: boolean;
    control: Control<any>;
    label?: any;
    costRounding: number;
    setValue: UseFormSetValue<any>;
    constructionTypeName: string;
    noLimitSpan?: boolean;
    inputClassName?: string;
    name: string;
    spanSelectionData?: IUerContractOtherCostLimitSpanSelectionData;
    isLimitSpanEditDisabled?: boolean;
    isFullWidth?: boolean;
    isEmptyPlaceholder?: boolean;
    notRequired?: boolean;
    onChange?: () => void;
}

export const ContractOtherCostLimitSpan: React.FC<IContractOtherCostLimitSpan> = (props) => {
    const {
        isDisabled,
        control,
        isChecked,
        isTooltipHidden,
        defaultConstruction,
        constructionTypeId,
        constructionTypeName,
        label,
        costRounding,
        setValue,
        noLimitSpan,
        inputClassName,
        name,
        spanSelectionData,
        isLimitSpanEditDisabled,
        isFullWidth,
        isEmptyPlaceholder = false,
        notRequired = false,
        onChange,
    } = props;

    const hasActualCost = useWatch({ control, name: `hasActualCost.${constructionTypeId}` });
    const hasLimitSpan = useWatch({ control, name: `hasLimitSpan.${constructionTypeId}` });

    const controlRef = useRef<HTMLDivElement>(null);

    const [isOpened, setIsOpened] = useState(false);

    const buttonClassName = useMemo(
        () =>
            cn(Styles.contractOtherCostLimitSpanButton, {
                [Styles.disabled]: isDisabled || hasActualCost,
            }),
        [hasActualCost, isDisabled],
    );

    const rules = useMemo(
        () => ({
            validate: validateLimitCost({
                isSelected: !isDisabled && !hasActualCost,
                constructionTypeId: !noLimitSpan ? constructionTypeId : undefined,
                hasLimitSpan,
            }),
        }),
        [constructionTypeId, hasActualCost, hasLimitSpan, isDisabled, noLimitSpan],
    );

    const className = useMemo(() => {
        const sName = noLimitSpan
            ? Styles.contractOtherCostNoLimitSpanInput
            : `${Styles.contractOtherCostLimitSpanInput} ${
                  !isDisabled && !hasActualCost && hasLimitSpan ? Styles.contractOtherCostLimitSpanInputEditEnabled : ''
              }`;

        return inputClassName ? `${inputClassName} ${sName}` : sName;
    }, [hasActualCost, hasLimitSpan, inputClassName, isDisabled, noLimitSpan]);

    const onToggleSpanAdditionType = useCallback(() => {
        const value = !hasLimitSpan;

        setValue(`hasLimitSpan.${constructionTypeId}`, value);

        if (!value) {
            setValue(`limitSpanInputs.${constructionTypeId}`, []);
        }
    }, [constructionTypeId, hasLimitSpan, setValue]);

    const onClose = useCallback(() => {
        setIsOpened(false);
    }, []);

    useEffect(() => {
        const target = hasLimitSpan
            ? (controlRef.current?.getElementsByClassName('IconEdit')?.[0] as HTMLSpanElement)
            : undefined;

        if (isLimitSpanEditDisabled && target) {
            target.style.color = 'var(--color-control-typo-disable) !important';
            target.style.cursor = 'not-allowed';
        } else if (target && target.style.cursor === 'not-allowed') {
            target.style.removeProperty('color');
            target.style.removeProperty('cursor');
        }

        const onClick = () => {
            if (!isLimitSpanEditDisabled) {
                setIsOpened(true);
            }
        };

        target?.addEventListener('click', onClick);

        return () => {
            target?.removeEventListener('click', onClick);
        };
    }, [hasLimitSpan, isLimitSpanEditDisabled]);

    const placeholder = useMemo(() => {
        if (hasLimitSpan) {
            return '0,00';
        }

        if (isDisabled || hasActualCost) {
            return isEmptyPlaceholder ? ' ' : '';
        }

        return 'Введите';
    }, [hasLimitSpan, isDisabled, hasActualCost, isEmptyPlaceholder]);

    return (
        <>
            <FieldGroup ref={controlRef} size="s">
                <ControlNumberField
                    name={`cost.${constructionTypeId}`}
                    label={label}
                    labelPosition="left"
                    control={control}
                    type="text"
                    size="s"
                    width={isFullWidth ? 'full' : 'default'}
                    required={(!isDisabled && !hasActualCost && defaultConstruction) || (isChecked && !notRequired)}
                    rules={rules}
                    disabled={isDisabled || hasActualCost || hasLimitSpan}
                    clearOnDisabled={!isChecked}
                    isTooltipHidden={isTooltipHidden}
                    decimalScale={costRounding || 0}
                    placeholder={placeholder}
                    className={className}
                    rightSide={!hasLimitSpan ? undefined : IconEdit}
                    direction="downCenter"
                    onChangeField={onChange}
                />
                {
                    (!noLimitSpan ? (
                        <TooltipWrapper
                            message={
                                <div className={Styles.message}>
                                    <div className="message-header">Способ добавления:</div>
                                    <div>
                                        {!hasLimitSpan
                                            ? 'Изменение итоговой стоимости договора'
                                            : 'Проведение в счёт других затрат'}
                                    </div>
                                </div>
                            }
                            size="m"
                            isInteractive={false}
                            className={Styles.tooltipWrapper}
                            toolTipClassName={Styles.tooltip}
                        >
                            <Button
                                className={buttonClassName}
                                view="ghost"
                                type="button"
                                iconLeft={!hasLimitSpan ? IconDocFilled : IconTree}
                                size="s"
                                iconSize="xs"
                                disabled={isDisabled || hasActualCost}
                                onClick={onToggleSpanAdditionType}
                            />
                        </TooltipWrapper>
                    ) : undefined) as any
                }
            </FieldGroup>
            {spanSelectionData && isOpened && (
                <ContractOtherCostLimitSpanModal
                    constructionTypeName={constructionTypeName}
                    constructionTypeId={constructionTypeId}
                    onClose={onClose}
                    name={name}
                    rules={rules}
                    control={control}
                    costRounding={costRounding}
                    setValue={setValue}
                    spanSelectionData={spanSelectionData}
                />
            )}
        </>
    );
};

ContractOtherCostLimitSpan.displayName = 'ContractOtherCostLimitSpan';
